import {useState, useEffect, useContext, useRef} from 'react';
import {Button, Form, Spin, Tooltip, Select, message} from 'antd';
import {QuestionCircleOutlined} from '@ant-design/icons';
import {SGeneralForms} from 'shj-components';
import styles from "../../../../index.module.less"
import {
  getBudGetEnums,
  getHouseDecorateStyleEnum,
  getHouseKindEnum,
  getHouseNatureEnum,
  getHouseStatusEnum,
  getHouseTypeEnum,
  getPermanentResidentsEnum,
  getProductionTypeEnum,
  getResidencePurposeEnum, getSignBillIntentEnum
} from "@/services/customerServices.ts";
import {useSetState} from "ahooks";
import {getEnums, getRealEstateList} from "@/services/homeService.ts";
import {getBizTypes} from "@/services/callCenter";
import {getUser} from "shj-utils";
import clueDetailContext from "@/pages/callTool/intelligentOutboundCall/components/Info/clueDetailContext.ts";
import dayjs from "dayjs";
import {isEmpty} from "lodash-es";
import {addClue, updateClue} from "@/services/saleOpServices.ts";
import {dateFormat} from "@/utils/date.ts";
import {useAtom} from "jotai";
import {callListAtom} from "@/pages/callTool/intelligentOutboundCall/components/Info/store";

function UserInfoModule() {
  const {detailInfo, isFreePhone} = useContext(clueDetailContext)
  const [callList, setCallList] = useAtom(callListAtom)
  const user = getUser()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false);
  const [dictionaryData, setDictionaryData] = useSetState<any>({});

  useEffect(() => {
    fetchDictionaryData()
  }, []);

  useEffect(() => {
    if (!isEmpty(detailInfo)) getInfo()
  }, [detailInfo])

  const safeSplit = (value, delimiter) => {
    return typeof value === 'string' && value ? value.split(delimiter) : [];
  }
  const safeJoin = (arr?: string[] | null, delimiter = ','): string => {
    return arr?.length ? arr.join(delimiter) : '';
  };
  const getInfo = async () => {
    form.resetFields()
    const {customerInfo, dtlInfo, houseInfo} = detailInfo
    const info = {
      ...detailInfo,
      customerInfo: {
        ...customerInfo,
        customerId: customerInfo.id,
      },
      houseInfo: {
        ...houseInfo,
        handoverDate: houseInfo.handoverDate ? dayjs(houseInfo.handoverDate) : null,
      },
      dtlInfo: {
        ...dtlInfo,
        expectRenovationDate: dtlInfo.expectRenovationDate ? dayjs(dtlInfo.expectRenovationDate) : null,
        estimatedRenovationTime: dtlInfo.estimatedRenovationTime ? dayjs(dtlInfo.estimatedRenovationTime) : null,
        lookingForwardToStayingTime: dtlInfo.lookingForwardToStayingTime ? dayjs(dtlInfo.lookingForwardToStayingTime) : null,
        painPoint: safeSplit(dtlInfo?.painPoint, '-'),
        itchPoint: safeSplit(dtlInfo?.itchPoint, '-'),
        breakPoint: safeSplit(dtlInfo?.breakPoint, '-'),
        blockPoint: safeSplit(dtlInfo?.blockPoint, '-'),
      },
    }
    form.setFieldsValue(info);
  }

  const fetchDictionaryData = async () => {
    setLoading(true)
    try {
      const resList: any = await Promise.allSettled([
        getRealEstateList({orgId: user.companyId}),
        getBizTypes(),
        getHouseDecorateStyleEnum(),
        getHouseKindEnum(),
        getHouseNatureEnum(),
        getHouseStatusEnum(),
        getHouseTypeEnum(),
        getPermanentResidentsEnum(),
        getProductionTypeEnum(),
        getResidencePurposeEnum(),
        getSignBillIntentEnum(),
        getBudGetEnums(),
        getEnums("plus-crm", "com.shj.saas.plus.crm.enums.clue.PainPoint"),
        getEnums("plus-crm", "com.shj.saas.plus.crm.enums.clue.ItchPoint"),
        getEnums("plus-crm", "com.shj.saas.plus.crm.enums.clue.BreakPoint"),
        getEnums("plus-crm", "com.shj.saas.plus.crm.enums.clue.BlockPoint"),
        getEnums("plus-crm", "com.shj.saas.plus.crm.enums.FunctionNeedEnum"),
      ]);

      const map: Record<string, any[]> = {
        buildList: resList?.[0]?.value instanceof Array ? resList?.[0]?.value : [],
        bizType: resList?.[1]?.value instanceof Array ? resList?.[1]?.value : [],
        designStyleCode: resList?.[2]?.value instanceof Array ? resList?.[2]?.value : [],
        houseKindCode: resList?.[3]?.value instanceof Array ? resList?.[3]?.value : [],
        houseNatureCode: resList?.[4]?.value instanceof Array ? resList?.[4]?.value : [],
        houseStatus: resList?.[5]?.value instanceof Array ? resList?.[5]?.value : [],
        layout: resList?.[6]?.value instanceof Array ? resList?.[6]?.value : [],
        permanentResidents: resList?.[7]?.value instanceof Array ? resList?.[7]?.value : [],
        intentionProductCode: resList?.[8]?.value instanceof Array ? resList?.[8]?.value : [],
        livePurpose: resList?.[9]?.value instanceof Array ? resList?.[9]?.value : [],
        signIntentionCode: resList?.[10]?.value instanceof Array ? resList?.[10]?.value : [],
        budget: resList?.[11]?.value instanceof Array ? resList?.[11]?.value : [],
        painPoint: resList?.[12]?.value instanceof Array ? resList?.[12]?.value : [],
        itchPoint: resList?.[13]?.value instanceof Array ? resList?.[13]?.value : [],
        breakPoint: resList?.[14]?.value instanceof Array ? resList?.[14]?.value : [],
        blockPoint: resList?.[15]?.value instanceof Array ? resList?.[15]?.value : [],
        functionNeed: resList?.[16]?.value instanceof Array ? resList?.[16]?.value : [],
      }
      setDictionaryData({...map})
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = () => {
    form.validateFields().then(async (values: any) => {
      setLoading(true);
      const apiFn = values.id ? updateClue : addClue;
      const _values = {
        ...values,
        id: values.id || detailInfo.id,
        dtlInfo: {
          ...values.dtlInfo,
          painPoint: safeJoin(values.dtlInfo?.painPoint, '-'),
          itchPoint: safeJoin(values.dtlInfo?.itchPoint, '-'),
          breakPoint: safeJoin(values.dtlInfo?.breakPoint, '-'),
          blockPoint: safeJoin(values.dtlInfo?.blockPoint, '-'),
          expectRenovationDate: dateFormat(values.dtlInfo?.expectRenovationDate, 'YYYY-MM-DD HH:mm:ss'),
          estimatedRenovationTime: dateFormat(values.dtlInfo?.estimatedRenovationTime, 'YYYY-MM-DD HH:mm:ss'),
          lookingForwardToStayingTime: dateFormat(values.dtlInfo?.lookingForwardToStayingTime, 'YYYY-MM-DD HH:mm:ss'),
        },
        houseInfo: {
          ...values.houseInfo,
          handoverDate: dateFormat(values.houseInfo?.handoverDate, 'YYYY-MM-DD HH:mm:ss'),
        }
      }
      apiFn(_values).then(res => {
        message.success(res.message || '操作成功');
        setCallList({...callList, reload: !callList.reload})
      }).catch(err => {
        message.error(err.message)
      }).finally(() => {
        setLoading(false);
      })
    }).catch((err) => {
      console.log(err)
      const errorStr = err.errorFields?.[0]?.errors?.[0]
      message.error(errorStr)
    })
  }

  return (
      <div className={styles.userInfoModule}>
        <Spin tip="Loading" size="small" spinning={loading}>
          <div style={{padding: '15px 15px 0'}}>
            <SGeneralForms
                formConfig={{form}}
                titleMode={'edit'}
                layout={'vertical'}
                // detailsData={{...detailInfo}}
                configs={[
                  {
                    title: '客户信息',
                    formItem: [
                      {label: 'id', name: 'id', hidden: true},
                      {label: 'customerId', name: ['customerInfo', 'customerId'], hidden: true},
                      {
                        name: ['customerInfo', 'name'],
                        label: '客户姓名',
                        spanCol: 12,
                        required: true,
                        rules: [{required: true, message: '不能为空'}],
                        comConfig: {disabled: isFreePhone}
                      },
                      {
                        name: ['customerInfo', 'gender'], label: '性别', spanCol: 12,
                        type: 'select',
                        comConfig: {
                          options: [{value: '男', label: '男'}, {value: '女', label: '女'}],
                          disabled: isFreePhone,
                        }
                      },
                    ],
                  },
                  {
                    title: '楼盘信息',
                    formItem: [
                      {
                        name: ['houseInfo', 'buildingId'], label: '楼盘', spanCol: 12,
                        type: 'select',
                        required: true,
                        rules: [{required: true, message: '不能为空'}],
                        comConfig: {
                          disabled: isFreePhone,
                          options: dictionaryData.buildList || [],
                          fieldNames: {label: 'name', value: 'id'}
                        }
                      },
                      {
                        name: ['houseInfo', 'doorNo'], label: '门牌号', spanCol: 12,
                        comConfig: {
                          disabled: isFreePhone,
                        }
                      },
                      {
                        name: ['houseInfo', 'houseNatureCode'], label: '房屋属性', spanCol: 12,
                        type: 'select',
                        comConfig: {
                          disabled: isFreePhone,
                          options: dictionaryData.houseNatureCode || [],
                          fieldNames: {label: 'description', value: 'key'}
                        }
                      },
                      {
                        name: ['houseInfo', 'houseStatus'], label: '房屋状态', spanCol: 12,
                        type: 'select',
                        comConfig: {
                          disabled: isFreePhone,
                          options: dictionaryData.houseStatus || [],
                          fieldNames: {label: 'description', value: 'key'}
                        }
                      },
                      {
                        name: ['houseInfo', 'houseKindCode'], label: '房屋类型', spanCol: 12,
                        type: 'select',
                        comConfig: {
                          disabled: isFreePhone,
                          options: dictionaryData.houseKindCode || [],
                          fieldNames: {label: 'description', value: 'key'}
                        }
                      },
                      {
                        name: ['houseInfo', 'layout'], label: '户型', spanCol: 12,
                        type: 'select',
                        comConfig: {
                          disabled: isFreePhone,
                          options: dictionaryData.layout || [],
                          fieldNames: {label: 'description', value: 'key'}
                        }
                      },
                      {
                        name: ['houseInfo', 'constructArea'], label: '建筑面积', spanCol: 12,
                        type: 'inputNumber',
                        comConfig: {
                          disabled: isFreePhone,
                          precision: 2,
                          min: 0.01,
                          max: 999999999.99,
                        }
                      },
                      {
                        name: ['houseInfo', 'innerArea'], label: '套内面积', spanCol: 12,
                        type: 'inputNumber',
                        comConfig: {
                          disabled: isFreePhone,
                          precision: 2,
                          min: 0.01,
                          max: 999999999.99,
                        }
                      },
                      {
                        name: ['houseInfo', 'houseHight'], label: '层高', spanCol: 12,
                        type: 'inputNumber',
                        comConfig: {
                          disabled: isFreePhone,
                          precision: 2,
                          min: 0.01,
                          max: 999999999.99,
                        }
                      },
                      {
                        name: ['houseInfo', 'handoverDate'], label: '交房日期', spanCol: 12, type: 'date',
                        comConfig: {
                          disabled: isFreePhone,
                        }
                      },
                    ]
                  },
                  {
                    title: '家空间需求',
                    formItem: [
                      {
                        name: ['dtlInfo', 'bizTypes'], label: '需求标签', spanCol: 12,
                        type: 'select',
                        required: true,
                        rules: [{required: true, message: '不能为空'}],
                        comConfig: {
                          disabled: isFreePhone,
                          mode: 'multiple',
                          options: dictionaryData.bizType || [],
                          fieldNames: {label: 'description', value: 'key'}
                        }
                      },
                      {
                        name: ['dtlInfo', 'budget'], label: '装修预算', spanCol: 12,
                        type: 'select',
                        comConfig: {
                          disabled: isFreePhone,
                          options: dictionaryData.budget || [],
                          fieldNames: {label: 'description', value: 'key'}
                        }
                      },
                      {
                        name: ['dtlInfo', 'designStyleCode'], label: '装修风格', spanCol: 12,
                        type: 'select',
                        comConfig: {
                          disabled: isFreePhone,
                          options: dictionaryData.designStyleCode || [],
                          fieldNames: {label: 'description', value: 'key'}
                        }
                      },
                      {
                        name: ['dtlInfo', 'livePurpose'], label: '居住目的', spanCol: 12,
                        type: 'select',
                        comConfig: {
                          disabled: isFreePhone,
                          options: dictionaryData.livePurpose || [],
                          fieldNames: {label: 'description', value: 'key'}
                        }
                      },
                      {
                        name: ['dtlInfo', 'permanentResidents'], label: '常住人口', spanCol: 12,
                        type: 'select',
                        comConfig: {
                          disabled: isFreePhone,
                          options: dictionaryData.permanentResidents || [],
                          fieldNames: {label: 'description', value: 'key'}
                        }
                      },
                      {
                        label: <>客户意向 <Tooltip title={` A类: 线索有效，且是企微好友，且有需求属性，且有装修预算，且有装修风格，且房屋状态为现房，短时间内能邀约到店
                                 B类: 线索有效，且是企微好友，且有需求属性，需要花稍微多一点的时间才能邀约到店
                                 C类: 线索有效，需要花大量时间才可能邀约到店`}
                                                   overlayStyle={{whiteSpace: 'pre-line'}}><QuestionCircleOutlined
                            style={{marginLeft: 5, color: 'rgba(0, 0, 0, 0.45)'}}/></Tooltip></>,
                        name: ['dtlInfo', 'signIntentionCode'],
                        spanCol: 12,
                        type: 'select',
                        comConfig: {
                          disabled: isFreePhone,
                          placeholder: '请选择客户意向',
                          options: dictionaryData.signIntentionCode || [],
                          fieldNames: {label: 'description', value: 'key'}
                        }
                      },
                      {
                        name: ['dtlInfo', 'intentionProductCode'], label: '产品意向', spanCol: 12,
                        type: 'select',
                        comConfig: {
                          disabled: isFreePhone,
                          options: dictionaryData.intentionProductCode || [],
                          fieldNames: {label: 'description', value: 'key'}
                        }
                      },
                      {
                        name: ['dtlInfo', 'houseDemand'], label: '功能需求', spanCol: 12,
                        type: 'select',
                        comConfig: {
                          disabled: isFreePhone,
                          options: dictionaryData.functionNeed || [],
                          fieldNames: {label: 'description', value: 'key'}
                        }
                      },
                      {
                        name: ['dtlInfo', 'expectRenovationDate'], label: '预计进场装修时间', spanCol: 12, type: 'date',
                        comConfig: {
                          disabled: isFreePhone,
                        }
                      },
                      {
                        name: ['dtlInfo', 'estimatedRenovationTime'],
                        label: '预计定装修时间',
                        spanCol: 12,
                        type: 'date',
                        comConfig: {
                          disabled: isFreePhone,
                        }
                      },
                      {
                        name: ['dtlInfo', 'lookingForwardToStayingTime'],
                        label: '期待入住时间',
                        spanCol: 12,
                        type: 'date',
                        comConfig: {
                          disabled: isFreePhone,
                        }
                      },
                      {
                        name: ['dtlInfo', 'painPoint'], label: '痛点', spanCol: 24,
                        children: <Select disabled={isFreePhone} mode={"multiple"} placeholder={'请选择痒点'}
                                          fieldNames={{label: 'description', value: 'key'}}
                                          options={dictionaryData['painPoint']} allowClear showSearch
                                          optionFilterProp={'description'}/>
                      },
                      {
                        name: ['dtlInfo', 'painPointRemark'], label: '痛点备注', spanCol: 24,
                        isDeps: true,
                        deps: (form) => form.getFieldValue(['dtlInfo', 'painPoint'])?.includes('PainPointOther'),
                        comConfig: {
                          disabled: isFreePhone,
                          maxLength: 200, showCount: true
                        }
                      },
                      {
                        spanCol: 24,
                        label: '痒点',
                        name: ['dtlInfo', 'itchPoint'],
                        children: <Select disabled={isFreePhone} mode={"multiple"} placeholder={'请选择痒点'}
                                          fieldNames={{label: 'description', value: 'key'}}
                                          options={dictionaryData['itchPoint']} allowClear showSearch
                                          optionFilterProp={'description'}/>
                      },
                      {
                        spanCol: 24,
                        label: '痒点备注',
                        name: ['dtlInfo', 'itchPointRemark'],
                        isDeps: true,
                        deps: (form) => form.getFieldValue(['dtlInfo', 'itchPoint'])?.includes('ItchyOther'),
                        comConfig: {
                          disabled: isFreePhone,
                          maxLength: 200,
                          showCount: true
                        }
                      },
                      {
                        spanCol: 24,
                        label: '障碍点',
                        name: ['dtlInfo', 'blockPoint'],
                        children: <Select disabled={isFreePhone} mode={"multiple"} placeholder={'请选择障碍点'}
                                          fieldNames={{label: 'description', value: 'key'}}
                                          options={dictionaryData['blockPoint']} allowClear showSearch
                                          optionFilterProp={'description'}/>
                      },
                      {
                        spanCol: 24,
                        label: '障碍点备注',
                        name: ['dtlInfo', 'blockPointRemark'],
                        isDeps: true,
                        deps: (form) => form.getFieldValue(['dtlInfo', 'blockPoint'])?.includes('ObstaclePointOther'),
                        comConfig: {
                          disabled: isFreePhone,
                          maxLength: 200,
                          showCount: true
                        }
                      },
                      {
                        spanCol: 24,
                        label: '爆破点',
                        name: ['dtlInfo', 'breakPoint'],
                        children: <Select disabled={isFreePhone} mode={"multiple"} placeholder={'请选择爆破点'}
                                          fieldNames={{label: 'description', value: 'key'}}
                                          options={dictionaryData['breakPoint']} allowClear showSearch
                                          optionFilterProp={'description'}/>
                      },
                      {
                        spanCol: 24,
                        label: '爆破点备注',
                        name: ['dtlInfo', 'breakPointRemark'],
                        isDeps: true,
                        deps: (form) => form.getFieldValue(['dtlInfo', 'breakPoint'])?.includes('BlastingPointOther'),
                        comConfig: {
                          disabled: isFreePhone,
                          maxLength: 200,
                          showCount: true
                        }
                      },
                      {
                        spanCol: 24,
                        label: '备注',
                        name: ['dtlInfo', 'remark'],
                        comConfig: {
                          disabled: isFreePhone,
                          maxLength: 200,
                        }
                      },
                    ],
                  },
                ]}
            />
          </div>
        </Spin>
        <div className={styles.userFormSaveBtn}>
          <Button disabled={isFreePhone || loading} onClick={getInfo} style={{marginRight: 10}}>取消</Button>
          <Button disabled={isFreePhone || loading} type={'primary'} onClick={onSubmit}>保存</Button>
        </div>
      </div>
  );
}

export default UserInfoModule;